import type { LucideIcon } from 'lucide-vue-next';
import {
    Crop,
    ImageDown,
    Images,
    Layers,
    Maximize2,
    PencilLine,
    RefreshCw,
    Shrink,
    Sparkles,
    SwatchBook,
} from 'lucide-vue-next';
import '@/lib/ensureWayfinderLocale';
import {
    bulkCompress,
    bulkResize,
    compress,
    convert,
    editor,
    heic,
    crop,
    grayscale,
    resize,
    rotate,
    removeBackground,
} from '@/routes/tools/image';

export type ImageMegaMenuItem = {
    key: string;
    title: string;
    href: string;
    description: string;
    icon: LucideIcon;
    iconClass: string;
};

export function isImageNavSectionActive(
    currentPath: string,
    items: ImageMegaMenuItem[],
): boolean {
    return items.some((item) => {
        const base = item.href.split('?')[0] ?? item.href;

        return currentPath === base || currentPath.startsWith(`${base}/`);
    });
}

/** Standalone nav entry (not grouped under Transform). */
export const editorImageNavItem: ImageMegaMenuItem = {
    key: 'editor',
    title: 'Edit image',
    href: editor.url(),
    description:
        'Crop, draw, filter, annotate, and watermark in one touch-friendly workflow—all in your browser.',
    icon: PencilLine,
    iconClass: 'text-blue-600 dark:text-blue-300',
};

export const transformImageNavItems: ImageMegaMenuItem[] = [
    {
        key: 'remove_background',
        title: 'Remove background',
        href: removeBackground.url(),
        description:
            'Erase the background in your browser; download a transparent PNG.',
        icon: Sparkles,
        iconClass: 'text-fuchsia-600 dark:text-fuchsia-300',
    },
    {
        key: 'resize',
        title: 'Resize image',
        href: resize.url(),
        description: 'Presets, fit modes, and long-edge scaling.',
        icon: Maximize2,
        iconClass: 'text-sky-600 dark:text-sky-300',
    },
    {
        key: 'crop',
        title: 'Crop image',
        href: crop.url(),
        description: 'Drag to select the area to keep.',
        icon: Crop,
        iconClass: 'text-emerald-600 dark:text-emerald-300',
    },
    {
        key: 'rotate',
        title: 'Rotate and flip',
        href: rotate.url(),
        description: 'Turn and mirror in one step.',
        icon: RefreshCw,
        iconClass: 'text-violet-600 dark:text-violet-300',
    },
    {
        key: 'grayscale',
        title: 'Grayscale',
        href: grayscale.url(),
        description: 'Convert to black and white.',
        icon: SwatchBook,
        iconClass: 'text-zinc-600 dark:text-zinc-300',
    },
];

export const optimizeImageNavItems: ImageMegaMenuItem[] = [
    {
        key: 'compress',
        title: 'Compress image',
        href: compress.url(),
        description: 'Smaller files with quality control.',
        icon: Shrink,
        iconClass: 'text-amber-600 dark:text-amber-300',
    },
    {
        key: 'convert',
        title: 'Convert format',
        href: convert.url(),
        description: 'PNG, JPEG, or WebP output.',
        icon: ImageDown,
        iconClass: 'text-rose-600 dark:text-rose-300',
    },
    {
        key: 'heic',
        title: 'HEIC to JPG',
        href: heic.url(),
        description: 'Convert iPhone HEIC photos to JPG, PNG, or WebP.',
        icon: ImageDown,
        iconClass: 'text-teal-600 dark:text-teal-300',
    },
];

export const bulkImageNavItems: ImageMegaMenuItem[] = [
    {
        key: 'bulk_resize',
        title: 'Bulk resize',
        href: bulkResize.url(),
        description: 'ZIP download for many images.',
        icon: Layers,
        iconClass: 'text-cyan-600 dark:text-cyan-300',
    },
    {
        key: 'bulk_compress',
        title: 'Bulk compress',
        href: bulkCompress.url(),
        description: 'Same compression settings for a batch.',
        icon: Images,
        iconClass: 'text-orange-600 dark:text-orange-300',
    },
];

export const allImageNavItems: ImageMegaMenuItem[] = [
    editorImageNavItem,
    ...transformImageNavItems,
    ...optimizeImageNavItems,
    ...bulkImageNavItems,
];
